import React, { useEffect, useMemo, useRef, useState } from 'react'
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Modal,
  Typography,
  useMediaQuery,
} from '@mui/material'
import TuneIcon from '@mui/icons-material/Tune'

import MetricDropdown from './components/MetricDropdown'
import { useActions, useAppSelector } from 'hooks'
import { countryType, inputFilter } from 'store/AIMetricOT/reducerSlice'
import { SubIndexCodes, viewType } from 'store/vibrancy/reducerSlice'
import YearDropdown from './components/YearDropdown'
import { ChartType, MetricGroup, MetricValue, Ranking, SortOrder, ViewType } from 'types'
import {
  CornerButton,
  CornerButtonViewType,
  LeftPanelSection,
  RoundButton,
  RoundButtonMetricValue,
  LeftPanelPillarSelector,
  RowSelectPillar,
  RowSelectSubPillar,
  RowExpandSelectPillar,
  RoundButtonATMOT,
  RoundButtonChartTypeATMOT,
  TooltipComponent,
  CornerButtonTools,
} from './LeftPanel.styled'
import { RangeSlider } from '../RangeSlider/MetricSlider'
import PillarSelector from './components/PillarSelector'
import { useWindowResize } from 'hooks/useWindowResize'
import { removePrefix } from 'utils/removePrefix'
import MultipleCountriesSelection from './components/MultipleCountriesSelection'
import { COLOR, PILLAR_INFO, SUB_INDICES_INFO } from 'constant'
import CountrySearchDropdown from './components/CountrySearchDropdown'
import { arrayOfMetricValue, arrayOfPillarValue, isAllMetricValueEmpty, isAllPillarValueEmpty } from 'utils'
import useOnClickOutside from 'hooks/useOnClickOutside'
import GroupingSearchDropdown from './components/GroupingSearchDropdown'
import SortByDropDown from './components/SortByDropDown'
import SubIndexSelector from './components/SubIndexSelector'
import { CloseCircleOutlined } from '@ant-design/icons'
import { debounce } from 'lodash'
import useDebouncedDispatch from 'hooks/useDebouncedDispatch'

const LeftPanel = () => {
  const isSmaller600 = useMediaQuery('(max-width:600px)')
  const isTooltip = false
  const [expandedMetricGroups, setExpandedMetricGroups] = React.useState<string[]>([])
  const [isOpenMobilePanel, setIsOpenMobilePanel] = React.useState<boolean>(true)
  const currentWidth = useWindowResize()
  const [warningDialogOpen, setWarningDialogOpen] = React.useState(false)
  const responsiveSidebarRef = useRef<HTMLDivElement>(null)
  const { loadMetricData } = useActions()
  useOnClickOutside(currentWidth < 600, responsiveSidebarRef, () => setIsOpenMobilePanel(false))
  const countryData = useAppSelector(({ vibrancy }) => vibrancy.countryData)
  const selectedMetricValue = useAppSelector(({ vibrancy }) => vibrancy.configuration.metricValue)
  const disabledPillarList = useAppSelector(({ vibrancy }) => vibrancy.disabledPillarList)
  const [modalOpen, setModalOpen] = useState(false)
  const [ismodalShown, setIsmodalShown] = useState(false)

  const handleClose = () => {
    setWarningDialogOpen(false)
  }

  const handleModalClose = () => {
    setModalOpen(false)
    setIsmodalShown(true)
  }

  useEffect(() => {
    if (currentWidth >= 600) {
      setIsOpenMobilePanel(true)
    }
    if (currentWidth < 600) {
      setIsOpenMobilePanel(false)
    }
  }, [currentWidth])

  const {
    setTool,
    setRanking,
    setCountryType,
    setMetricOTValue,
    setMetricOTChartType,
    setCurrentYear,
    setMetricValue,
    setViewType,
    setMultiplierMetricGroup,
    setMultiplierMetric,
    resetAll,
    checkMetrices,
    equalize,
    setSubIndex,
  } = useActions()
  const currentFilter = useAppSelector(inputFilter)
  const {
    tool: currentTool,
    country: currentCountry,
    countryType: currentCountryType,
    ranking: currentRanking,
    metricValue: currentAIMOTMetricValue,
    chartType: currentAIMOTChartType,
  } = currentFilter
  const currentViewType = useAppSelector(viewType)
  const yearFilter = useAppSelector(({ vibrancy }) => vibrancy.configuration.yearFilter)
  const vibrancyMetricValue = useAppSelector(({ vibrancy }) => vibrancy.configuration.metricValue)
  const metricGroups = useAppSelector(({ vibrancy }) => vibrancy.metricGroup)
  const selectedSubIndex = useAppSelector(({ vibrancy }) => vibrancy.selectedSubIndex)
  const yearRange = yearFilter.yearRange.map((el, key) => ({ key, label: el }))
  const [metricGroupLocal, setMetricGroupLocal] = useState<MetricGroup[]>()
  const getDebouncedDispatcher = useDebouncedDispatch(500)
  const [rankingLocal, setRankingLocal] = useState<Ranking>()
  const [countryTypeLocal, setCountryTypeLocal] = useState<Ranking>()
  const [viewTypeLocal, setViewTypeLocal] = useState<ViewType>()
  const [currentYearLocal, setCurrentYearLocal] = useState<number>()
  const [metricValueLocal, setMetricValueLocal] = useState<MetricValue>()

  useEffect(() => {
    setMetricGroupLocal(metricGroups)
  }, [metricGroups])

  useEffect(() => {
    setRankingLocal(currentRanking)
  }, [currentRanking])

  useEffect(() => {
    setCountryTypeLocal(currentCountryType)
  }, [currentCountryType])

  useEffect(() => {
    setViewTypeLocal(currentViewType)
  }, [currentViewType])

  useEffect(() => {
    setCurrentYearLocal(yearFilter?.currentYear)
  }, [yearFilter?.currentYear])

  const handleMetricGroupSliderChange = (arg: { metricGroupName: string; value: number }) => {
    const { metricGroupName, value } = arg
    const mg = metricGroupLocal?.find((el) => el.metricGroupName === metricGroupName)
    const filteredMetricGroup = metricGroupLocal?.filter((i) => i.metricGroupName !== metricGroupName)
    if (filteredMetricGroup && mg) {
      setMetricGroupLocal([...filteredMetricGroup, { ...mg, multiplier: value }])

      const debouncedUpdate = getDebouncedDispatcher(setMultiplierMetricGroup)
      debouncedUpdate({
        metricGroupName,
        value,
      })
    }
  }

  const handleYearSelection = (value: number) => {
    setCurrentYearLocal(value)
    const debouncedUpdate = getDebouncedDispatcher(setCurrentYear)
    debouncedUpdate(value)
  }

  const handleMetricSliderChange = (args: { metricGroupName: string; metricName: string; value: number }) => {
    const { metricGroupName, metricName, value } = args

    let refinedName = ''
    if (metricName.startsWith('pc_')) {
      refinedName = metricName.replace('pc_', '').trim()
    } else {
      refinedName = 'pc_'.concat(metricName).trim()
    }
    const mg = metricGroupLocal?.find((el) => el.metricGroupName === metricGroupName)

    const filteredMetricGroup = metricGroupLocal?.filter((el) => el.metricGroupName !== metricGroupName)

    const newMetrics = mg?.metrics.map((metric) => {
      if (metric.shortname_raw === metricName || metric.shortname_raw === refinedName) {
        return { ...metric, multiplier: value }
      } else {
        return { ...metric }
      }
    })

    if (filteredMetricGroup && mg && newMetrics) {
      setMetricGroupLocal([...filteredMetricGroup, { ...mg, metrics: newMetrics }])
      const debouncedUpdate = getDebouncedDispatcher(setMultiplierMetric)
      debouncedUpdate({
        metricGroupName,
        metricName,
        value,
      })
    }
  }

  const retrieveMetricBaseOnMetricValue = (metricGroupName: string) => {
    if (metricGroupLocal) {
      const metrics: MetricGroup['metrics'] = metricGroupLocal.find(
        (metricGroup: MetricGroup) => metricGroup.metricGroupName === metricGroupName,
      )!.metrics
      switch (vibrancyMetricValue) {
        case 'absolute':
          return metrics.filter((metric) => !metric.metricName.includes(' PC'))
        case 'perCapita':
          return metrics.filter((metric) => metric.metricName.includes(' PC'))
      }
    }
  }

  const handleAIVRTool = () => {
    setTool({ tool: 'AIVR' })
    setCountryType({ countryType: 'global' })
  }

  const handleAIMOTTool = () => {
    setTool({ tool: 'AIMOT' })
    setCountryType({ countryType: 'national' })
  }

  const handleRanking = (value: Ranking) => {
    setRankingLocal(value)
    setCountryTypeLocal(value)

    const debouncedRankingUpdate = getDebouncedDispatcher(setRanking)
    debouncedRankingUpdate({ ranking: value })

    const debouncedTypeUpdate = getDebouncedDispatcher(setCountryType)
    debouncedTypeUpdate({ countryType: value })
  }

  const handleAIMOTMetricValue = (value: MetricValue) => {
    setMetricOTValue({ metricValue: value })
  }

  const handleAIMOTChartType = (value: ChartType) => {
    setMetricOTChartType({ chartType: value })
  }

  const handleMetric = (value: MetricValue) => {
    setMetricValueLocal(value)
    const debouncedUpdate = getDebouncedDispatcher(setMetricValue)
    debouncedUpdate(value)
  }

  const handleViewType = (value: ViewType) => {
    setViewTypeLocal(value)
    const debouncedUpdate = getDebouncedDispatcher(setViewType)
    debouncedUpdate(value)
  }

  const toggleMetricGroup = (metricGroupName: string) => {
    setExpandedMetricGroups((prev) =>
      prev.includes(metricGroupName) ? prev.filter((group) => group !== metricGroupName) : [...prev, metricGroupName],
    )
  }
  const handleMobile = () => {
    setIsOpenMobilePanel(!isOpenMobilePanel)
  }

  const areAllMetricPillarsZero = (metricGroupsParam: MetricGroup[]) => {
    return !metricGroupsParam.some((metricGroup) => metricGroup.multiplier !== 0)
  }

  const handleSelectSubIndex = (value: SubIndexCodes) => {
    setSubIndex(value)
  }

  useEffect(() => {
    rankingLocal === 'subindices' && !ismodalShown && setModalOpen(true)
  }, [rankingLocal])

  useEffect(() => {
    checkMetrices()
  }, [yearFilter?.currentYear, selectedMetricValue])

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: '12px', width: '100%' }}>
      <Dialog
        open={warningDialogOpen}
        onClose={handleClose}
        aria-labelledby='alert-dialog-title'
        aria-describedby='alert-dialog-description'
        slotProps={{ backdrop: { sx: { background: 'rgba(0, 0, 0, 0)' } } }}
      >
        <DialogTitle id='alert-dialog-title'>{'Warning'}</DialogTitle>
        <DialogContent>
          <DialogContentText id='alert-dialog-description'>
            Please assign at least one pillar weight to see the global vibrancy ranking
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose} autoFocus style={{ color: COLOR.TEXT, backgroundColor: COLOR.DARK }}>
            OK
          </Button>
        </DialogActions>
      </Dialog>
      <LeftPanelSection header='Tools' isTooltip={isTooltip}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: '12px', width: '100%', marginTop: '12px' }}>
          <CornerButtonTools
            style={{
              background: currentTool === 'AIVR' ? COLOR.DARK : COLOR.LIGHT,
              color: currentTool === 'AIVR' ? COLOR.TEXT : COLOR.DARK,
              fontFamily: `'CircularStdBold', sans-serif`,
            }}
            onClick={handleAIVRTool}
          >
            {isTooltip && (
              <Box sx={{ position: 'absolute', right: '2px', top: '2px' }}>
                <TooltipComponent
                  iconColor={currentTool === 'AIVR' ? COLOR.LIGHT : COLOR.DARK}
                  content={'Tooltip Content'}
                />
              </Box>
            )}
            GLOBAL AND NATIONAL AI VIBRANCY RANKINGS
          </CornerButtonTools>
          <CornerButtonTools
            style={{
              background: currentTool === 'AIMOT' ? COLOR.DARK : COLOR.LIGHT,
              color: currentTool === 'AIMOT' ? COLOR.TEXT : COLOR.DARK,
              fontFamily: `'CircularStdBold', sans-serif`,
            }}
            onClick={handleAIMOTTool}
          >
            {isTooltip && (
              <Box sx={{ position: 'absolute', right: '2px', top: '2px' }}>
                <TooltipComponent
                  iconColor={currentTool === 'AIMOT' ? COLOR.LIGHT : COLOR.DARK}
                  content={'Tooltip Content'}
                />
              </Box>
            )}
            AI METRICS OVER TIME
          </CornerButtonTools>
        </Box>
      </LeftPanelSection>
      <Box sx={{ position: 'relative' }}>
        {isSmaller600 && !isOpenMobilePanel && (
          <Box
            onClick={handleMobile}
            onKeyDown={handleMobile} // Added this because of SonarLint error
            sx={{
              cursor: 'pointer',
              width: '39px',
              height: '39px',
              border: `2px solid ${COLOR.NAVY}`,
              borderRadius: '50%',
              padding: '2px',
            }}
          >
            <TuneIcon style={{ color: COLOR.NAVY, width: '30px', height: '30px' }} />
          </Box>
        )}
        {isOpenMobilePanel && (
          <Box
            className='responsive-left-sidebar'
            ref={responsiveSidebarRef}
            sx={{
              zIndex: 99,
              position: isSmaller600 ? 'absolute' : 'relative',
              border: isSmaller600 ? `2px solid ${COLOR.NAVY}` : 'none',
              padding: isSmaller600 ? '12px' : 0,
              background: COLOR.WHITE,
              borderRadius: '16px',
              maxHeight: { xs: '450px', sm: '100%' },
              overflow: { xs: 'scroll', sm: 'unset' },
              minWidth: { xs: `${currentWidth - 50}px`, sm: 'unset' },
            }}
          >
            {isSmaller600 && (
              <Box onClick={handleMobile} sx={{ cursor: 'pointer' }}>
                <TuneIcon style={{ color: COLOR.NAVY, width: '30px', height: '30px' }} />
              </Box>
            )}
            {currentTool === 'AIVR' && (
              <>
                {viewTypeLocal === 'bar' ? (
                  <LeftPanelSection header='Ranking'>
                    <Box sx={{ display: 'flex', gap: '16px', margin: '8px 0px', flexWrap: 'wrap' }}>
                      <CornerButton
                        style={{
                          background: rankingLocal === 'global' ? COLOR.DARK : COLOR.LIGHT,
                          color: rankingLocal === 'global' ? COLOR.TEXT : COLOR.DARK,
                        }}
                        onClick={() => handleRanking('global')}
                      >
                        GLOBAL
                      </CornerButton>
                      <CornerButton
                        style={{
                          background: rankingLocal === 'national' ? COLOR.DARK : COLOR.LIGHT,
                          color: rankingLocal === 'national' ? COLOR.TEXT : COLOR.DARK,
                        }}
                        onClick={() => handleRanking('national')}
                      >
                        NATIONAL
                      </CornerButton>
                      <CornerButton
                        style={{
                          background: rankingLocal === 'subindices' ? COLOR.DARK : COLOR.LIGHT,
                          color: rankingLocal === 'subindices' ? COLOR.TEXT : COLOR.DARK,
                        }}
                        onClick={() => handleRanking('subindices')}
                      >
                        SUB-INDICES
                      </CornerButton>
                    </Box>
                  </LeftPanelSection>
                ) : (
                  <Box sx={{ height: { xs: '0px', sm: '0px' } }}></Box>
                )}
                {rankingLocal !== 'subindices' && viewTypeLocal && (
                  <LeftPanelSection header='View type'>
                    <Box sx={{ display: 'flex', gap: '16px', margin: '8px 0px', flexWrap: 'wrap' }}>
                      <CornerButtonViewType
                        disabled={areAllMetricPillarsZero(metricGroups)}
                        $customType='bar'
                        $currentType={viewTypeLocal}
                        onClick={() => handleViewType('bar')}
                      >
                        BAR
                      </CornerButtonViewType>
                      <CornerButtonViewType
                        disabled={areAllMetricPillarsZero(metricGroups)}
                        $customType='table'
                        $currentType={viewTypeLocal}
                        onClick={() => handleViewType('table')}
                      >
                        TABLE
                      </CornerButtonViewType>
                      <CornerButtonViewType
                        disabled={areAllMetricPillarsZero(metricGroups)}
                        $customType='slope'
                        $currentType={viewTypeLocal}
                        onClick={() => handleViewType('slope')}
                      >
                        SLOPE
                      </CornerButtonViewType>
                    </Box>
                  </LeftPanelSection>
                )}
                {countryTypeLocal === 'national' && viewTypeLocal === 'bar' ? (
                  <LeftPanelSection header='Sort by'>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: { xs: '2px', sm: '16px' } }}>
                      <Box>
                        <SortByDropDown width={208} height={32} defaultOrder={SortOrder.groupByPillar} />
                      </Box>
                    </Box>
                  </LeftPanelSection>
                ) : null}
                {viewTypeLocal !== 'slope' && (
                  <LeftPanelSection header='Filters'>
                    <Box
                      sx={{ display: 'flex', flexWrap: 'wrap', gap: { xs: '2px', sm: '16px' }, alignItems: 'center' }}
                    >
                      <YearDropdown
                        yearList={yearRange?.reverse()}
                        currentYear={currentYearLocal ?? yearFilter?.currentYear}
                        onClick={handleYearSelection}
                        width={viewTypeLocal === 'table' ? 145 : 145}
                      />
                      {rankingLocal === 'national' && viewTypeLocal !== 'table' && (
                        <Box sx={{ marginTop: '4px' }}>
                          <CountrySearchDropdown width={208} height={32} defaultCountry={currentCountry} />
                        </Box>
                      )}
                      {rankingLocal === 'global' && viewTypeLocal === 'bar' && (
                        <Box>
                          <GroupingSearchDropdown width={150} />
                        </Box>
                      )}
                    </Box>
                  </LeftPanelSection>
                )}
                {((rankingLocal !== 'subindices' && viewTypeLocal === 'bar' && countryTypeLocal !== 'national') ||
                  viewTypeLocal === 'slope') && (
                  <LeftPanelPillarSelector header='Select pillar' subheader='Select weights'>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: '24px', marginTop: '24px' }}>
                      {PILLAR_INFO.map((pillarName, indexPillar) => {
                        const metricGroup = metricGroupLocal?.find(
                          (metricGroupEl: MetricGroup) => metricGroupEl.metricGroupName === pillarName.name,
                        )
                        let disabledPillar = disabledPillarList.find(
                          (i) => i.metricGroupNameDatakey === metricGroup?.metricGroupNameDatakey,
                        )
                          ? true
                          : false
                        return (
                          <div key={metricGroup?.metricGroupName ?? indexPillar}>
                            <RowSelectPillar>
                              <PillarSelector
                                metricType={pillarName.displayName?.toUpperCase()}
                                weightValue={metricGroup ? metricGroup.multiplier * 100 : 0}
                                onClick={() =>
                                  metricGroup ? toggleMetricGroup(metricGroup.metricGroupName) : undefined
                                }
                                isOpen={
                                  metricGroup && !disabledPillar
                                    ? expandedMetricGroups.includes(metricGroup.metricGroupName)
                                    : false
                                }
                                isDisabled={disabledPillar}
                              />
                              <RangeSlider
                                metricGroupName={metricGroup?.metricGroupName as string}
                                isSubSlider={false}
                                value={metricGroup ? metricGroup.multiplier * 100 : 0}
                                isDisabled={disabledPillar}
                                onChange={(value) => {
                                  if (!metricGroup) return
                                  handleMetricGroupSliderChange({
                                    metricGroupName: metricGroup.metricGroupName,
                                    value: value / 100,
                                  })
                                }}
                                onChangeCommitted={() => {
                                  if (
                                    arrayOfPillarValue(metricGroups).length !== 0 &&
                                    isAllPillarValueEmpty(metricGroups)
                                  )
                                    setWarningDialogOpen(true)
                                }}
                              />
                            </RowSelectPillar>
                            {metricGroup &&
                              expandedMetricGroups.includes(metricGroup.metricGroupName) &&
                              !disabledPillar && (
                                <RowExpandSelectPillar>
                                  {retrieveMetricBaseOnMetricValue(metricGroup.metricGroupName)?.map((metric) => {
                                    const cutMetricName = removePrefix(metric.metricName, 'Number of ')
                                    return (
                                      <div key={metric.metricName}>
                                        <RowSelectSubPillar>
                                          <span>{cutMetricName}</span>
                                          <RangeSlider
                                            metricGroupName={metricGroup.metricGroupName}
                                            isSubSlider={true}
                                            value={metric.multiplier * 100}
                                            isDisabled={disabledPillar}
                                            onChange={(value) => {
                                              handleMetricSliderChange({
                                                metricGroupName: metricGroup.metricGroupName,
                                                metricName: metric.shortname_raw as string,
                                                value: value / 100,
                                              })
                                            }}
                                            onChangeCommitted={() => {
                                              if (
                                                arrayOfMetricValue(metricGroups, vibrancyMetricValue).length !== 0 &&
                                                isAllMetricValueEmpty(metricGroups, vibrancyMetricValue)
                                              )
                                                setWarningDialogOpen(true)
                                            }}
                                          />
                                        </RowSelectSubPillar>
                                      </div>
                                    )
                                  })}
                                </RowExpandSelectPillar>
                              )}
                          </div>
                        )
                      })}
                      <Box
                        sx={{
                          width: '100%',
                          display: 'flex',
                          justifyContent: 'flex-end',
                          gap: '20px',
                        }}
                      >
                        <RoundButton
                          style={{
                            width: '130px',
                            fontSize: '14px',
                            margin: '8px 0px',
                            display: rankingLocal !== 'global' && viewTypeLocal !== 'slope' ? 'none' : undefined,
                            background: COLOR.LIGHT,
                          }}
                          onClick={() => resetAll()}
                        >
                          RESET ALL
                        </RoundButton>
                        <RoundButton
                          style={{
                            width: '130px',
                            fontSize: '14px',
                            margin: '8px 0px',
                            display: rankingLocal !== 'global' && viewTypeLocal !== 'slope' ? 'none' : undefined,
                            background: COLOR.LIGHT,
                          }}
                          onClick={() => equalize()}
                        >
                          EQUALIZE
                        </RoundButton>
                      </Box>
                    </Box>
                  </LeftPanelPillarSelector>
                )}
                {rankingLocal === 'subindices' && (
                  <LeftPanelPillarSelector header='Select sub-index'>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: '24px', marginTop: '24px' }}>
                      {SUB_INDICES_INFO.map((eachIndex) => (
                        <SubIndexSelector
                          key={eachIndex.indexCode}
                          subIndexName={eachIndex.indexName}
                          subIndexCode={eachIndex.indexCode}
                          isSelected={eachIndex.indexCode === selectedSubIndex}
                          isDisabled={false}
                          onClick={() => handleSelectSubIndex(eachIndex.indexCode)}
                        />
                      ))}
                    </Box>
                  </LeftPanelPillarSelector>
                )}
                {viewTypeLocal === 'table' && <MultipleCountriesSelection />}
                <LeftPanelSection header='Metrics values'>
                  <Box sx={{ display: 'flex', gap: '16px', margin: '8px 0px' }}>
                    <RoundButtonMetricValue
                      type='perCapita'
                      $currentType={metricValueLocal ?? vibrancyMetricValue}
                      onClick={() => handleMetric('perCapita')}
                    >
                      PER CAPITA
                    </RoundButtonMetricValue>
                    <RoundButtonMetricValue
                      type='absolute'
                      $currentType={metricValueLocal ?? vibrancyMetricValue}
                      onClick={() => handleMetric('absolute')}
                    >
                      ABSOLUTE
                    </RoundButtonMetricValue>
                  </Box>
                </LeftPanelSection>
              </>
            )}

            {currentTool === 'AIMOT' && (
              <>
                {currentAIMOTChartType === 'bar' && (
                  <LeftPanelSection header='Select country'>
                    <Box sx={{ marginTop: '10px' }}>
                      <CountrySearchDropdown width={210} height={32} defaultCountry={currentCountry} />
                    </Box>
                  </LeftPanelSection>
                )}
                <LeftPanelSection header='Select metric'>
                  <MetricDropdown />
                </LeftPanelSection>
                <LeftPanelSection header='Chart type'>
                  <Box sx={{ display: 'flex', gap: '16px', margin: '8px 0px' }}>
                    <RoundButtonChartTypeATMOT
                      $chartType='map'
                      $currentType={currentAIMOTChartType}
                      onClick={() => handleAIMOTChartType('map')}
                    >
                      WORLD MAP
                    </RoundButtonChartTypeATMOT>
                    <RoundButtonChartTypeATMOT
                      $chartType='bar'
                      $currentType={currentAIMOTChartType}
                      onClick={() => handleAIMOTChartType('bar')}
                    >
                      BAR CHART
                    </RoundButtonChartTypeATMOT>
                  </Box>
                </LeftPanelSection>
                <LeftPanelSection header='Metrics values'>
                  <Box sx={{ display: 'flex', gap: '16px', margin: '8px 0px' }}>
                    <RoundButtonATMOT
                      $type='perCapita'
                      $currentType={currentAIMOTMetricValue}
                      onClick={() => handleAIMOTMetricValue('perCapita')}
                    >
                      PER CAPITA
                    </RoundButtonATMOT>
                    <RoundButtonATMOT
                      $type='absolute'
                      $currentType={currentAIMOTMetricValue}
                      onClick={() => handleAIMOTMetricValue('absolute')}
                    >
                      ABSOLUTE
                    </RoundButtonATMOT>
                  </Box>
                </LeftPanelSection>
              </>
            )}
          </Box>
        )}
      </Box>
      <Modal
        open={!ismodalShown && modalOpen}
        onClose={handleModalClose}
        aria-labelledby='modal-modal-title'
        aria-describedby='modal-modal-description'
      >
        <Box sx={{ width: '90%', bgcolor: '#fff' }} className='subIndicesBox'>
          <Typography id='modal-modal-title' variant='h5' component='h2'>
            Sub-Indices
          </Typography>
          <Typography id='modal-modal-description' sx={{ mt: 2 }}>
            Explore the sub-indices for a more detailed perspective on AI vibrancy across selected domains. These
            sub-indices reveal how national strengths in AI vary across innovation, economic competitiveness, policy,
            governance, and public engagement.
          </Typography>
          <button onClick={handleModalClose} className='closeModal'>
            <CloseCircleOutlined />
          </button>
        </Box>
      </Modal>
    </Box>
  )
}

export default LeftPanel
